/**
 * Copia de seguridad portátil `.finvoz.json` (revisión de ADR OQ-1, 2026-09-10):
 * IndexedDB local es la fuente de autoridad; el backup es el mecanismo de
 * portabilidad entre dispositivos/perfiles mientras no exista sincronización
 * automática (diferida a la fase APK).
 *
 * Allowlist estricta por diseño: `FinvozBackupPayload` solo admite los campos
 * declarados aquí (cuentas, asientos, presupuestos, metas, cotizaciones,
 * ajustes no sensibles). No existe campo para token de acceso, token de
 * refresco, audio ni cookie — no es una omisión de tiempo de ejecución, es
 * estructural: `exportBackup` solo lee de las colecciones del almacén local
 * que nunca contienen esos datos (el token vive solo en memoria, closure de
 * `TokenStore`, y el audio nunca se persiste — spec/03-data-model.md §10, INV-8).
 */

import type { Account } from "../core/account.js";
import type { Budget, Goal } from "../core/budget.js";
import type { ChainedEntry } from "../integrity/chain.js";
import type { PriceQuote } from "../market/quote-provider.js";
import { sha256Hex } from "../integrity/hash.js";
import type { LocalStore, StoredItem } from "../storage/local-store.js";

export const BACKUP_SCHEMA_VERSION = 1;

const ACCOUNTS_COLLECTION = "accounts";
const ENTRIES_COLLECTION = "entries";
const BUDGETS_GOALS_COLLECTION = "budgets_goals";
const PRICE_QUOTES_COLLECTION = "price_quotes";

function quoteKey(instrumentId: string, asOf: string): string {
  return `${instrumentId}|${asOf}`;
}

function isBudget(v: Budget | Goal): v is Budget {
  return "limitMinor" in v;
}

export interface BackupSettings {
  readonly theme?: "system" | "light" | "dark";
  readonly googleClientId?: string;
  readonly spreadsheetId?: string | null;
}

export interface FinvozBackupPayload {
  readonly schemaVersion: number;
  readonly exportedAt: string; // instante UTC ISO-8601
  readonly profile: { readonly profileId: string; readonly name: string };
  readonly accounts: readonly Account[];
  readonly entries: readonly ChainedEntry[];
  readonly budgets: readonly Budget[];
  readonly goals: readonly Goal[];
  readonly priceQuotes: readonly PriceQuote[];
  readonly settings: BackupSettings;
}

export interface FinvozBackupFile {
  readonly payload: FinvozBackupPayload;
  /** sha256 hex sobre la serializacion canonica (claves ordenadas) de `payload`. */
  readonly checksum: string;
}

export class InvalidBackupFileError extends Error {
  constructor(reason: string) {
    super(`Archivo de copia de seguridad inválido: ${reason}`);
    this.name = "InvalidBackupFileError";
  }
}

export class ChecksumMismatchError extends Error {
  constructor() {
    super("El checksum del archivo no coincide con su contenido: el archivo está corrupto o fue alterado.");
    this.name = "ChecksumMismatchError";
  }
}

export class FutureBackupSchemaVersionError extends Error {
  constructor(found: number, known: number) {
    super(
      `El archivo declara version de esquema de backup ${found}, superior a la conocida ${known}. ` +
        `No se importa: una version futura podria contener campos que esta version no entiende.`,
    );
    this.name = "FutureBackupSchemaVersionError";
  }
}

// --- Serializacion determinista, segura para bigint (JSON nativo no admite bigint) ---

const BIGINT_TAG = "__bigint__";

function canonicalize(value: unknown): unknown {
  if (typeof value === "bigint") return { [BIGINT_TAG]: value.toString() };
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(canonicalize);
  const obj = value as Record<string, unknown>;
  const sorted: Record<string, unknown> = {};
  for (const key of Object.keys(obj).sort()) {
    sorted[key] = canonicalize(obj[key]);
  }
  return sorted;
}

function decanonicalize(value: unknown): unknown {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(decanonicalize);
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length === 1 && keys[0] === BIGINT_TAG && typeof obj[BIGINT_TAG] === "string") {
    return BigInt(obj[BIGINT_TAG] as string);
  }
  const out: Record<string, unknown> = {};
  for (const key of keys) out[key] = decanonicalize(obj[key]);
  return out;
}

function canonicalStringify(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

// --- Validacion estricta de forma: rechaza claves y estructuras desconocidas
// antes de importar nada (FR-BACKUP-4). No basta con que el tipo TypeScript
// declare los campos permitidos — eso es solo compile-time. Un archivo externo
// con checksum recalculado por un atacante podria incluir claves adicionales
// (por ejemplo "accessToken") que un tipeo laxo en tiempo de ejecucion
// aceptaria en silencio. Esta validacion es la barrera runtime: cualquier
// clave fuera del allowlist, en cualquier nivel de anidamiento, rechaza el
// archivo completo antes del checksum y antes de leer schemaVersion.

const ALLOWED_KEYS = {
  root: ["payload", "checksum"],
  payload: ["schemaVersion", "exportedAt", "profile", "accounts", "entries", "budgets", "goals", "priceQuotes", "settings"],
  profile: ["profileId", "name"],
  account: ["accountId", "name", "type", "currency", "parentId", "archived", "instrumentId"],
  chainedEntry: ["entry", "deviceId", "deviceSeq", "createdAt", "entryHash", "deviceChainHash"],
  entry: ["entryId", "date", "description", "origin", "reversesEntryId", "postings"],
  posting: ["index", "accountId", "amountMinor", "currency", "scale", "quantityMinor", "instrumentId"],
  budget: ["budgetId", "accountId", "limitMinor", "currency", "scale", "period", "thresholdPct", "archived"],
  goal: ["goalId", "name", "accountId", "targetMinor", "currency", "scale", "targetDate", "archived"],
  priceQuote: ["instrumentId", "asOf", "priceMinor", "currency", "scale", "source", "retrievedAt", "manual"],
  settings: ["theme", "googleClientId", "spreadsheetId"],
} as const;

function assertKnownKeysOnly(value: unknown, allowed: readonly string[], path: string): void {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return;
  const unknownKeys = Object.keys(value).filter((k) => !(allowed as readonly string[]).includes(k));
  if (unknownKeys.length > 0) {
    throw new InvalidBackupFileError(`clave(s) no reconocida(s) en '${path}': ${unknownKeys.join(", ")}`);
  }
}

function assertIsArray(value: unknown, path: string): asserts value is unknown[] {
  if (!Array.isArray(value)) {
    throw new InvalidBackupFileError(`'${path}' debe ser una lista`);
  }
}

/**
 * Recorre todo el payload validando que ningun objeto, a ningun nivel,
 * contenga una clave fuera de su allowlist declarado. Lanza en el primer
 * hallazgo — no intenta reparar ni ignorar silenciosamente lo desconocido.
 */
function assertPayloadShapeIsKnown(payload: Record<string, unknown>): void {
  assertKnownKeysOnly(payload, ALLOWED_KEYS.payload, "payload");

  if (payload.profile !== undefined) assertKnownKeysOnly(payload.profile, ALLOWED_KEYS.profile, "payload.profile");
  if (payload.settings !== undefined) assertKnownKeysOnly(payload.settings, ALLOWED_KEYS.settings, "payload.settings");

  assertIsArray(payload.accounts, "payload.accounts");
  payload.accounts.forEach((a, i) => assertKnownKeysOnly(a, ALLOWED_KEYS.account, `payload.accounts[${i}]`));

  assertIsArray(payload.entries, "payload.entries");
  payload.entries.forEach((e, i) => {
    assertKnownKeysOnly(e, ALLOWED_KEYS.chainedEntry, `payload.entries[${i}]`);
    const chained = e as Record<string, unknown>;
    if (chained.entry !== undefined) {
      assertKnownKeysOnly(chained.entry, ALLOWED_KEYS.entry, `payload.entries[${i}].entry`);
      const inner = chained.entry as Record<string, unknown>;
      if (inner.postings !== undefined) {
        assertIsArray(inner.postings, `payload.entries[${i}].entry.postings`);
        inner.postings.forEach((p, j) =>
          assertKnownKeysOnly(p, ALLOWED_KEYS.posting, `payload.entries[${i}].entry.postings[${j}]`),
        );
      }
    }
  });

  if (payload.budgets !== undefined) {
    assertIsArray(payload.budgets, "payload.budgets");
    payload.budgets.forEach((b, i) => assertKnownKeysOnly(b, ALLOWED_KEYS.budget, `payload.budgets[${i}]`));
  }
  if (payload.goals !== undefined) {
    assertIsArray(payload.goals, "payload.goals");
    payload.goals.forEach((g, i) => assertKnownKeysOnly(g, ALLOWED_KEYS.goal, `payload.goals[${i}]`));
  }
  if (payload.priceQuotes !== undefined) {
    assertIsArray(payload.priceQuotes, "payload.priceQuotes");
    payload.priceQuotes.forEach((q, i) => assertKnownKeysOnly(q, ALLOWED_KEYS.priceQuote, `payload.priceQuotes[${i}]`));
  }
}

// --- Exportacion ---

export interface ExportBackupParams {
  readonly localStore: LocalStore;
  readonly profile: { readonly profileId: string; readonly name: string };
  readonly settings: BackupSettings;
  readonly now?: string;
}

/** Lee las colecciones del perfil activo y construye el payload de backup con su checksum. */
export async function exportBackup(params: ExportBackupParams): Promise<FinvozBackupFile> {
  const accounts = (await params.localStore.list<Account>(ACCOUNTS_COLLECTION)).map((i) => i.value);
  const entries = (await params.localStore.list<ChainedEntry>(ENTRIES_COLLECTION)).map((i) => i.value);
  const budgetsGoalsRaw = (await params.localStore.list<Budget | Goal>(BUDGETS_GOALS_COLLECTION)).map((i) => i.value);
  const budgets = budgetsGoalsRaw.filter(isBudget);
  const goals = budgetsGoalsRaw.filter((v): v is Goal => !isBudget(v));
  const priceQuotes = (await params.localStore.list<PriceQuote>(PRICE_QUOTES_COLLECTION)).map((i) => i.value);

  const payload: FinvozBackupPayload = {
    schemaVersion: BACKUP_SCHEMA_VERSION,
    exportedAt: params.now ?? new Date().toISOString(),
    profile: params.profile,
    accounts,
    entries,
    budgets,
    goals,
    priceQuotes,
    settings: params.settings,
  };

  const checksum = await sha256Hex(canonicalStringify(payload));
  return { payload, checksum };
}

/** Texto `.finvoz.json` listo para descargar: JSON legible, determinista, seguro para bigint. */
export function serializeBackupFile(file: FinvozBackupFile): string {
  return JSON.stringify(canonicalize(file), null, 2);
}

/** Parsea y valida estrictamente un archivo de backup: JSON valido, version conocida, checksum coincidente. */
export async function parseBackupFile(text: string): Promise<FinvozBackupFile> {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new InvalidBackupFileError("el contenido no es JSON válido");
  }

  const decoded = decanonicalize(raw) as { payload?: unknown; checksum?: unknown } | null;
  if (!decoded || typeof decoded !== "object" || Array.isArray(decoded)) {
    throw new InvalidBackupFileError("la raíz del archivo debe ser un objeto");
  }
  assertKnownKeysOnly(decoded, ALLOWED_KEYS.root, "(raíz)");
  if (!decoded.payload || typeof decoded.payload !== "object" || Array.isArray(decoded.payload)) {
    throw new InvalidBackupFileError("falta el campo 'payload'");
  }
  if (typeof decoded.checksum !== "string" || decoded.checksum.length === 0) {
    throw new InvalidBackupFileError("falta el campo 'checksum'");
  }

  // Validacion estructural estricta ANTES de leer schemaVersion o verificar
  // checksum: un archivo con claves desconocidas se rechaza siempre, aunque
  // su checksum sea internamente consistente con su propio contenido alterado.
  assertPayloadShapeIsKnown(decoded.payload as Record<string, unknown>);

  const payload = decoded.payload as FinvozBackupPayload;
  if (typeof payload.schemaVersion !== "number") {
    throw new InvalidBackupFileError("falta 'payload.schemaVersion'");
  }
  if (payload.schemaVersion > BACKUP_SCHEMA_VERSION) {
    throw new FutureBackupSchemaVersionError(payload.schemaVersion, BACKUP_SCHEMA_VERSION);
  }
  if (!Array.isArray(payload.accounts) || !Array.isArray(payload.entries)) {
    throw new InvalidBackupFileError("faltan 'payload.accounts' o 'payload.entries'");
  }

  const recomputed = await sha256Hex(canonicalStringify(payload));
  if (recomputed !== decoded.checksum) {
    throw new ChecksumMismatchError();
  }

  return { payload, checksum: decoded.checksum };
}

// --- Importacion ---

export type ImportMode = "replace" | "merge" | "new-profile";

export interface ImportBackupParams {
  readonly file: FinvozBackupFile;
  readonly mode: ImportMode;
  /**
   * Almacén del perfil destino, ya seleccionado/creado por el llamador.
   * En modo "replace" se vacía antes de escribir; en "new-profile" se asume
   * vacío (perfil recién creado); en "merge" se combina evitando duplicados
   * por identificador estable.
   */
  readonly targetLocalStore: LocalStore;
}

export interface ImportBackupResult {
  readonly mode: ImportMode;
  readonly accountsWritten: number;
  readonly entriesWritten: number;
  readonly entriesSkippedAsDuplicate: number;
  readonly budgetsWritten: number;
  readonly goalsWritten: number;
  readonly priceQuotesWritten: number;
}

async function clearCollections(store: LocalStore, collections: readonly string[]): Promise<void> {
  for (const collection of collections) {
    const existing = await store.list(collection);
    for (const item of existing) await store.delete(collection, item.id);
  }
}

export async function importBackup(params: ImportBackupParams): Promise<ImportBackupResult> {
  const { file, mode, targetLocalStore } = params;
  const payload = file.payload;

  if (mode === "replace") {
    await clearCollections(targetLocalStore, [
      ACCOUNTS_COLLECTION,
      ENTRIES_COLLECTION,
      BUDGETS_GOALS_COLLECTION,
      PRICE_QUOTES_COLLECTION,
    ]);
  }

  if (mode === "merge") {
    const existingAccounts = await targetLocalStore.list<Account>(ACCOUNTS_COLLECTION);
    const existingAccountIds = new Set(existingAccounts.map((i) => i.id));
    const newAccounts = payload.accounts.filter((a) => !existingAccountIds.has(a.accountId));
    await targetLocalStore.putBatch(
      ACCOUNTS_COLLECTION,
      newAccounts.map((a): StoredItem<Account> => ({ id: a.accountId, value: a })),
    );

    const existingEntries = await targetLocalStore.list<ChainedEntry>(ENTRIES_COLLECTION);
    const existingEntryIds = new Set(existingEntries.map((i) => i.id));
    let entriesSkipped = 0;
    const newEntries = payload.entries.filter((e) => {
      if (existingEntryIds.has(e.entry.entryId)) {
        entriesSkipped += 1;
        return false;
      }
      return true;
    });
    await targetLocalStore.putBatch(
      ENTRIES_COLLECTION,
      newEntries.map((e): StoredItem<ChainedEntry> => ({ id: e.entry.entryId, value: e })),
    );

    const existingBG = await targetLocalStore.list(BUDGETS_GOALS_COLLECTION);
    const existingBGIds = new Set(existingBG.map((i) => i.id));
    const newBudgets = payload.budgets.filter((b) => !existingBGIds.has(b.budgetId));
    const newGoals = payload.goals.filter((g) => !existingBGIds.has(g.goalId));
    const newBudgetsGoalsItems: StoredItem<Budget | Goal>[] = [
      ...newBudgets.map((b): StoredItem<Budget | Goal> => ({ id: b.budgetId, value: b })),
      ...newGoals.map((g): StoredItem<Budget | Goal> => ({ id: g.goalId, value: g })),
    ];
    await targetLocalStore.putBatch(BUDGETS_GOALS_COLLECTION, newBudgetsGoalsItems);

    const existingQuotes = await targetLocalStore.list<PriceQuote>(PRICE_QUOTES_COLLECTION);
    const existingQuoteIds = new Set(existingQuotes.map((i) => i.id));
    const newQuotes = payload.priceQuotes.filter((q) => !existingQuoteIds.has(quoteKey(q.instrumentId, q.asOf)));
    await targetLocalStore.putBatch(
      PRICE_QUOTES_COLLECTION,
      newQuotes.map((q): StoredItem<PriceQuote> => ({ id: quoteKey(q.instrumentId, q.asOf), value: q })),
    );

    return {
      mode,
      accountsWritten: newAccounts.length,
      entriesWritten: newEntries.length,
      entriesSkippedAsDuplicate: entriesSkipped,
      budgetsWritten: newBudgets.length,
      goalsWritten: newGoals.length,
      priceQuotesWritten: newQuotes.length,
    };
  }

  // "replace" (ya vaciado arriba) y "new-profile" (destino recien creado, vacio): escritura directa.
  await targetLocalStore.putBatch(
    ACCOUNTS_COLLECTION,
    payload.accounts.map((a): StoredItem<Account> => ({ id: a.accountId, value: a })),
  );
  await targetLocalStore.putBatch(
    ENTRIES_COLLECTION,
    payload.entries.map((e): StoredItem<ChainedEntry> => ({ id: e.entry.entryId, value: e })),
  );
  const allBudgetsGoalsItems: StoredItem<Budget | Goal>[] = [
    ...payload.budgets.map((b): StoredItem<Budget | Goal> => ({ id: b.budgetId, value: b })),
    ...payload.goals.map((g): StoredItem<Budget | Goal> => ({ id: g.goalId, value: g })),
  ];
  await targetLocalStore.putBatch(BUDGETS_GOALS_COLLECTION, allBudgetsGoalsItems);
  await targetLocalStore.putBatch(
    PRICE_QUOTES_COLLECTION,
    payload.priceQuotes.map((q): StoredItem<PriceQuote> => ({ id: quoteKey(q.instrumentId, q.asOf), value: q })),
  );

  return {
    mode,
    accountsWritten: payload.accounts.length,
    entriesWritten: payload.entries.length,
    entriesSkippedAsDuplicate: 0,
    budgetsWritten: payload.budgets.length,
    goalsWritten: payload.goals.length,
    priceQuotesWritten: payload.priceQuotes.length,
  };
}
